import { useMemo } from "react";
import type { HermesAction, ResultsSource } from "@containers/Hermes/hooks/useActionPanel";
import { knownTotal } from "@containers/Hermes/hooks/useOfferCount";
import BookmarkCard, { toBookmarkCompanies } from "./BookmarkCard";
import ContactsCard from "./ContactsCard";
import DownloadCard from "./DownloadCard";
import WorkspaceCard from "./WorkspaceCard";
import CreditConfirmationCard from "./CreditConfirmationCard";

type ActionCardProps = {
  action: HermesAction;
  onClose: () => void;
  // Opens the full table beside the chat, in place of this card.
  onCustomise?: (source: ResultsSource) => void;
  // Sends a message to the chat, with the confirm token of a priced lookup the user approved.
  onSend?: (text: string, confirmToken?: string) => void;
};

/** The card for an action a message's action bar opened. ChatShell renders it in place of the composer. */
export default function ActionCard({ action, onClose, onCustomise, onSend }: ActionCardProps) {
  // "Customise selection" only when there is more to choose from than the answer's table
  // shows (or the count is not known yet), and not on rows the user already ticked.
  const source = action.source;
  const total = source ? knownTotal(source.download) : null;
  const exceedsPreview = Boolean(source && source.previewCount > 0 && (total === null || total > source.previewCount));
  // What the card's heading counts: the result's records, or the rows the answer shows when that is all there is.
  const totalRecords = source ? (total ?? (source.previewCount > 0 ? source.previewCount : undefined)) : undefined;
  // The companies the answer's table shows, for the pill on a download card opened beneath it.
  const shownCompanies = useMemo(() => toBookmarkCompanies(source?.contacts?.companies ?? []).map((company) => ({ key: company.company_id, name: company.name })), [source]);
  const customise = source && onCustomise && !action.fromSelection && exceedsPreview ? () => onCustomise(source as ResultsSource) : undefined;

  switch (action.kind) {
    case "bookmark":
      return <BookmarkCard block={action.block} onClose={onClose} modal={action.fromSelection} onCustomise={customise} totalRecords={totalRecords} />;
    case "workspace":
      return <WorkspaceCard block={action.block} messageId={action.messageId} onClose={onClose} modal={action.fromSelection} onCustomise={customise} totalRecords={totalRecords} />;
    case "contacts":
      return <ContactsCard block={action.block} conversationId={action.conversationId} messageId={action.messageId} onClose={onClose} modal={action.fromSelection} onCustomise={customise} totalRecords={totalRecords} />;
    case "download":
      return <DownloadCard shownCompanies={shownCompanies} block={action.block} onClose={onClose} modal={action.fromSelection} previewCount={action.source?.previewCount} initialRange={action.initialRange} companyIds={action.companyIds} rowKeys={action.rowKeys} lockRange={action.fromSelection} onCustomise={customise} />;
    case "credit":
      return <CreditConfirmationCard block={action.block} onClose={onClose} onConfirm={onSend} />;
    default:
      return null;
  }
}
